import React, { useCallback, useState } from 'react';
import { Alert, Box } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import PictureAsPdfOutlined from '@mui/icons-material/PictureAsPdfOutlined';
import { downloadDischargeInstructionsPdf } from 'services/documentsService';

/**
 * A patient's own copy of one set of discharge instructions, as a PDF.
 *
 * Used on the page a scanned QR opens and on the patient's Discharge Instructions
 * page. On the QR page it deliberately competes with the account offer: these are
 * medical instructions, and "sign up or risk losing your warning signs" is not a
 * trade we make. Outlined, so the account stays the obvious next step there.
 *
 * `placement`: "top" is right-aligned above the instructions (the QR page);
 * "bottom" is left-aligned after them, closing out one visit in a list of visits.
 */
const DischargePdfButton = ({ instructions, placement = 'top' }) => {
  const atBottom = placement === 'bottom';
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');

  const handleDownload = useCallback(async () => {
    setDownloading(true);
    setError('');
    try {
      await downloadDischargeInstructionsPdf(instructions);
    } catch (err) {
      console.error('[DischargePdfButton] PDF download failed', err);
      setError('Could not download the PDF. Please try again.');
    } finally {
      setDownloading(false);
    }
  }, [instructions]);

  const errorAlert = error && (
    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
      {error}
    </Alert>
  );

  return (
    <>
      {atBottom && errorAlert}
      <Box
        sx={{
          display: 'flex',
          justifyContent: atBottom ? 'flex-start' : 'flex-end',
          mb: atBottom ? 0 : 2,
        }}
      >
        <LoadingButton
          variant="outlined"
          size="small"
          startIcon={<PictureAsPdfOutlined />}
          loading={downloading}
          onClick={() => void handleDownload()}
        >
          Download PDF
        </LoadingButton>
      </Box>
      {!atBottom && errorAlert}
    </>
  );
};

export default DischargePdfButton;
